<template>
  <div class="intro-card">
    <!-- 内容区：左侧视频 + 右侧模块 -->
    <div class="intro-content">
      <!-- 左侧视频（精致边框+阴影） -->
      <div class="intro-video">
        <div class="video-wrapper">
          <video
              :src="props.data.video"
              controls
              poster="https://picsum.photos/id/1062/400/300"
              class="video-player"
          >
            您的浏览器不支持视频播放
          </video>
        </div>
      </div>

      <!-- 右侧三个模块（卡片式质感） -->
      <div class="intro-modules">
        <!-- 大赛主题模块 -->
        <div class="module theme-module" style="padding: 20px">
          <div class="module-title">
            <span>大赛主题</span>
            <span class="arrow">➤</span>
          </div>
          <p class="module-desc">{{ getModuleContent("theme") }}</p>
        </div>

        <!-- 参赛优势 + 参赛福利 并列模块 -->
        <div class="module-row">
          <div class="module advantage-module" style="padding: 20px">
            <div class="module-title">
              <span class="icon">🔊</span>
              <span>参赛优势</span>
            </div>
            <ul class="module-list">
              <li v-for="(item, idx) in getModuleContent('advantage')" :key="idx">
                <span class="list-dot"></span>{{ item }}
              </li>
            </ul>
          </div>

          <div class="module welfare-module" style="padding: 20px">
            <div class="module-title">
              <span class="icon">🔗</span>
              <span>参赛福利</span>
            </div>
            <ul class="module-list">
              <li v-for="(item, idx) in getModuleContent('welfare')" :key="idx">
                <span class="list-dot"></span>{{ item }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps } from 'vue';

const props = defineProps({
  data: { type: Object, required: true },
  theme: { type: Object, required: true }
});

// 根据模块类型获取对应内容
const getModuleContent = (type) => {
  const module = props.data.modules.find(m => m.type === type);
  return module ? module.content : "";
};
</script>

<style scoped lang="scss">
// 核心变量：高级简约配色
$primary: #E6398B;       // 点缀色（不突兀）
$bg-white: #FFFFFF;      // 背景白
$text-dark: #2D3748;     // 深灰标题（更高级）
$text-gray: #666666;     // 中灰内容（按要求统一）
$border-light: #F5F5F5;  // 极浅灰边框（不生硬）
$shadow: 0 4px 12px rgba(0, 0, 0, 0.03); // 弱阴影（有层次不杂乱）
$shadow-hover: 0 6px 16px rgba(0, 0, 0, 0.05);
$radius: 10px;           // 圆润圆角（不尖锐）
$transition: all 0.25s ease;

// 卡片容器：干净无多余样式
.intro-card {
  background: $bg-white;
  border-radius: $radius;
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
}

// 内容区布局：比例协调，间距舒适
.intro-content {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  flex-wrap: wrap;
}

// 左侧视频：精致包裹
.intro-video {
  flex: 1;
  min-width: 300px;

  .video-wrapper {
    background: $bg-white;
    border: 1px solid $border-light;
    border-radius: $radius;
    padding: 8px;
    box-shadow: $shadow;
    transition: $transition;

    &:hover {
      box-shadow: $shadow-hover;
    }
  }

  .video-player {
    width: 100%;
    max-width: 400px;
    border-radius: $radius - 2px;
    border: none;
    background: #000;
    aspect-ratio: 16/9; // 固定宽高比（不拉伸）
    object-fit: cover;
  }
}

// 右侧模块容器：间距合理
.intro-modules {
  flex: 1.8;
  min-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// 通用模块样式：卡片质感
.module {
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: $radius;
  padding: 22px;
  box-shadow: $shadow;
  transition: $transition;

  &:hover {
    box-shadow: $shadow-hover;
    transform: translateY(-2px);
  }

  .module-title {
    font-size: 18px;
    font-weight: 600;
    color: $primary;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // 按要求统一文字样式
  .module-desc {
    color: #666;
    font-size: 15px;
    line-height: 1.8;
    margin: 0 0 15px 0;
    letter-spacing: 0.1px;
  }

  .module-list {
    color: #666;
    font-size: 15px;
    line-height: 1.8;
    list-style: none;
    padding: 0;
    margin: 0 0 15px 0;

    li {
      position: relative;
      padding-left: 22px;
      // 去掉li的hover动效
    }
  }

  .list-dot {
    position: absolute;
    left: 0;
    top: 11px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $primary;
    // 去掉dot的hover动效
  }
}

// 主题模块箭头
.theme-module .arrow {
  color: $primary;
  font-size: 18px;
  margin-left: auto;
  opacity: 0.7;
  // 去掉箭头hover动效
}

// 并列模块布局
.module-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.module-row .module {
  flex: 1;
  min-width: 240px;
}

// 图标样式
.icon {
  color: $primary;
  font-size: 19px;
  // 去掉图标hover动效
}

// 响应式适配（不挤压）
@media (max-width: 992px) {
  .intro-content {
    gap: 24px;
  }

  .intro-modules {
    min-width: 100%;
  }
}

@media (max-width: 768px) {
  .intro-card {
    padding: 24px;
  }

  .module {
    padding: 18px;
  }

  .module-title {
    font-size: 17px;
  }

  .module-desc, .module-list {
    font-size: 14px;
  }

  .module-row {
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .video-player {
    max-width: 100%;
  }

  .module-row .module {
    min-width: 100%;
  }
}
</style>